import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ChevronRight, Shield, Truck, CheckCircle, LogOut, Settings, ArrowDown } from 'lucide-react';
import { useCustomer } from '../../context/CustomerContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_CFG = {
  new:                  { label: 'Order Received',       color: 'text-zinc-400',  dot: 'bg-zinc-500' },
  payment_pending:      { label: 'Payment Pending',       color: 'text-yellow-400', dot: 'bg-yellow-500' },
  payment_verified:     { label: 'Payment Confirmed',     color: 'text-blue-400',   dot: 'bg-blue-500' },
  validation_pending:   { label: 'Under Review',          color: 'text-purple-400', dot: 'bg-purple-500' },
  validation_hold:      { label: 'Needs Attention',       color: 'text-red-400',    dot: 'bg-red-500' },
  ready_for_production: { label: 'Queued for Production', color: 'text-green-400',  dot: 'bg-green-500' },
  batched:              { label: 'Scheduled',             color: 'text-cyan-400',   dot: 'bg-cyan-500' },
  in_production:        { label: 'In Production',         color: 'text-blue-400',   dot: 'bg-blue-400' },
  serialized:           { label: 'Serial # Assigned',     color: 'text-purple-400', dot: 'bg-purple-400' },
  ready_to_ship:        { label: 'Ready to Ship',         color: 'text-orange-400', dot: 'bg-orange-500' },
  shipped:              { label: 'Shipped',               color: 'text-teal-400',   dot: 'bg-teal-500' },
  cancelled:            { label: 'Cancelled',             color: 'text-zinc-600',   dot: 'bg-zinc-700' },
};

const PIPELINE_ORDER = ['new','payment_pending','payment_verified','validation_pending','validation_hold',
  'ready_for_production','batched','in_production','serialized','packed','ready_to_ship','shipped'];

// Mini 5-dot progress bar on each order card
function MiniPipeline({ status }) {
  const steps = ['new','payment_verified','in_production','serialized','shipped'];
  const curIdx = PIPELINE_ORDER.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-2">
      {steps.map((s, i) => {
        const stepIdx = PIPELINE_ORDER.indexOf(s);
        const done = curIdx >= stepIdx;
        return (
          <div key={s} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${done ? 'bg-orange-500' : 'bg-zinc-700'}`} />
            {i < steps.length - 1 && <div className={`w-4 h-px ${done && curIdx > stepIdx ? 'bg-orange-500/50' : 'bg-zinc-800'}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function AccountDashboard() {
  const { customer, logout, token, loading: customerLoading } = useCustomer();
  const navigate = useNavigate();
  const ordersRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'in_progress' | 'shipped'

  useEffect(() => {
    if (customerLoading) return;
    if (!customer) { navigate('/account/login'); return; }
    if (!token) return;
    fetch(`${BASE}/api/customer/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [token, customer, customerLoading]);

  const handleLogout = () => { logout(); navigate('/'); };

  const scrollToOrders = (filter) => {
    setActiveFilter(filter);
    setTimeout(() => {
      ordersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const inProgress = orders.filter(o => !['shipped', 'cancelled'].includes(o.status));
  const shipped = orders.filter(o => o.status === 'shipped');

  const filteredOrders = activeFilter === 'in_progress' ? inProgress
    : activeFilter === 'shipped' ? shipped
    : orders;

  if (customerLoading) return null;
  if (!customer) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-3xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-bebas text-4xl text-white tracking-wider">MY ACCOUNT</h1>
            <p className="font-inter text-sm text-zinc-500 mt-1">
              Welcome back, {customer.first_name} {customer.last_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/account/profile"
              className="flex items-center gap-1.5 font-inter text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors">
              <Settings size={13} />Profile
            </Link>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 font-inter text-xs text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors">
              <LogOut size={13} />Sign Out
            </button>
          </div>
        </div>

        {/* Clickable stat cards — filter and scroll to orders */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            {
              filter: 'all',
              label: 'Total Orders',
              val: orders.length,
              color: 'text-white',
              activeColor: 'border-white/40 bg-white/5',
              hint: 'View all orders',
            },
            {
              filter: 'in_progress',
              label: 'In Progress',
              val: inProgress.length,
              color: 'text-orange-400',
              activeColor: 'border-orange-500/60 bg-orange-500/8',
              hint: 'Track active orders',
            },
            {
              filter: 'shipped',
              label: 'Shipped',
              val: shipped.length,
              color: 'text-teal-400',
              activeColor: 'border-teal-500/50 bg-teal-500/5',
              hint: 'View delivered orders',
            },
          ].map(s => (
            <button
              key={s.filter}
              data-testid={`stat-${s.filter}`}
              onClick={() => scrollToOrders(s.filter)}
              className={`bg-[#1a1a1a] border p-4 text-center transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer group ${
                activeFilter === s.filter ? s.activeColor : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className={`font-bebas text-3xl tracking-wider ${s.color}`}>{s.val}</div>
              <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</div>
              <div className={`flex items-center justify-center gap-1 mt-1.5 font-inter text-[9px] transition-colors ${
                activeFilter === s.filter ? s.color : 'text-zinc-700 group-hover:text-zinc-500'
              }`}>
                <ArrowDown size={8} />
                {s.hint}
              </div>
            </button>
          ))}
        </div>

        {/* FFL dealer preference */}
        {customer.preferred_ffl_dealer?.business_name && (
          <div className="bg-[#1a1a1a] border border-white/10 p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield size={16} className="text-orange-500 flex-shrink-0" />
              <div>
                <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">Preferred FFL Transfer Dealer</p>
                <p className="font-inter text-sm text-white font-semibold">{customer.preferred_ffl_dealer.business_name}</p>
                <p className="font-inter text-xs text-zinc-500">
                  {[customer.preferred_ffl_dealer.city, customer.preferred_ffl_dealer.state].filter(Boolean).join(', ')}
                </p>
              </div>
            </div>
            <Link to="/account/profile" className="font-inter text-xs text-orange-400 hover:text-orange-300 transition-colors">
              Change →
            </Link>
          </div>
        )}

        {/* Orders section */}
        <div ref={ordersRef}>
          {/* Filter header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bebas text-2xl text-white tracking-wider">
              {activeFilter === 'in_progress' ? 'ORDERS IN PROGRESS' :
               activeFilter === 'shipped'     ? 'SHIPPED ORDERS' :
                                                'ALL ORDERS'}
              {filteredOrders.length > 0 && (
                <span className="font-inter text-sm text-zinc-500 ml-3 normal-case tracking-normal">
                  ({filteredOrders.length})
                </span>
              )}
            </h2>
            {activeFilter !== 'all' && (
              <button onClick={() => setActiveFilter('all')}
                className="font-inter text-xs text-zinc-500 hover:text-white transition-colors">
                Show all →
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-14 bg-[#1a1a1a] border border-white/10">
              <Package size={36} className="text-zinc-700 mx-auto mb-3" />
              {activeFilter === 'all' ? (
                <>
                  <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO ORDERS YET</p>
                  <p className="font-inter text-sm text-zinc-600 mt-1">Your orders will appear here after purchase</p>
                  <Link to="/shop" className="inline-flex items-center gap-2 mt-5 font-bebas text-base tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors">
                    SHOP NOW →
                  </Link>
                </>
              ) : (
                <>
                  <p className="font-bebas text-xl text-zinc-600 tracking-wider">
                    NO {activeFilter === 'shipped' ? 'SHIPPED' : 'IN-PROGRESS'} ORDERS
                  </p>
                  <button onClick={() => setActiveFilter('all')} className="mt-4 font-inter text-xs text-orange-400 hover:text-orange-300 transition-colors">
                    ← View all orders
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredOrders.map(order => {
                const cfg = STATUS_CFG[order.status] || STATUS_CFG.new;
                const hasNfa = order.has_nfa_items;
                const totalQty = order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
                const dateStr = order.created_at
                  ? new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—';
                return (
                  <Link
                    key={order._id}
                    to={`/account/orders/${order._id}`}
                    data-testid={`order-card-${order._id}`}
                    className="block bg-[#1a1a1a] border border-white/10 hover:border-orange-500/40 transition-all group"
                  >
                    <div className="flex items-center gap-4 p-4">
                      {/* Order icon */}
                      <div className="w-10 h-10 bg-[#0f0f0f] border border-white/10 flex items-center justify-center flex-shrink-0">
                        {order.status === 'shipped' ? (
                          <Truck size={16} className="text-teal-400" />
                        ) : (
                          <Package size={16} className="text-orange-500" />
                        )}
                      </div>

                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-bebas text-lg text-white tracking-wider group-hover:text-orange-400 transition-colors">
                            {order.order_number}
                          </span>
                          {hasNfa && (
                            <span className="flex items-center gap-1 font-inter text-[9px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5">
                              <Shield size={8} />NFA
                            </span>
                          )}
                          <span className={`flex items-center gap-1 font-inter text-[9px] font-bold ${cfg.color}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            {cfg.label}
                          </span>
                        </div>
                        <p className="font-inter text-xs text-zinc-500">
                          {totalQty} item{totalQty !== 1 ? 's' : ''} — {order.items?.map(i => i.product_name).join(', ')}
                        </p>
                        <MiniPipeline status={order.status} />
                      </div>

                      {/* Right side: date + price + arrow */}
                      <div className="text-right flex-shrink-0">
                        <p className="font-bebas text-lg text-white tracking-wide">${order.subtotal?.toFixed(2)}</p>
                        <p className="font-inter text-[10px] text-zinc-600 mt-0.5">{dateStr}</p>
                        <ChevronRight size={14} className="text-zinc-600 group-hover:text-orange-400 transition-colors ml-auto mt-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="font-inter text-xs text-zinc-600 hover:text-zinc-400 transition-colors">← Back to store</Link>
        </div>
      </div>
    </div>
  );
}
